import type {
  ApiCompatibility,
  AppConnector,
  AuthMethod,
  CompatibilityBadge,
  GatewayApiType,
  GatewayFormState,
  GatewayStatus,
  GovernanceSettings,
  LlmProvider,
  LlmType,
  RegisteredLlm,
  ServingProtocol,
} from './llmGatewayTypes';

export const LLM_GATEWAY_PAGE_ID = 'b7c4d1e8-9f02-4a56-b38c-148e5c9d6fa7';

export const LLM_GATEWAY_BASE_PATH = '/portal/ai-gateway/llm';
export const LLM_GATEWAY_PATHS = {
  list: LLM_GATEWAY_BASE_PATH,
  create: `${LLM_GATEWAY_BASE_PATH}/new`,
  detail: (id: string) => `${LLM_GATEWAY_BASE_PATH}/${id}`,
  edit: (id: string) => `${LLM_GATEWAY_BASE_PATH}/${id}/edit`,
} as const;

export const LLM_GATEWAY_STORAGE_KEY = 'selfic.llm-gateway.v1';
export const FABRIC_HEADER_EYEBROW = 'AI Gateway Fabric';
export const GATEWAY_HOST = 'https://gateway.selfic.ai';

export const WIZARD_STEPS = [
  { id: 'select-llm', label: 'Select LLM' },
  { id: 'api-type', label: 'Gateway API Type' },
  { id: 'governance', label: 'Governance & Auth' },
  { id: 'review', label: 'Review & Create' },
] as const;

export const LLM_TYPE_OPTIONS: { value: LlmType; label: string; hint: string }[] = [
  { value: 'chat', label: 'Chat', hint: 'Conversational completion models' },
  { value: 'embedding', label: 'Embedding', hint: 'Vector embedding models' },
  { value: 'multimodal', label: 'Multimodal', hint: 'Text plus image or audio' },
  { value: 'reranking', label: 'Reranking', hint: 'Retrieval ranking models' },
  { value: 'custom', label: 'Custom', hint: 'Internally registered serving' },
];

export const PROVIDER_OPTIONS: { value: LlmProvider; label: string }[] = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'azure_openai', label: 'Azure OpenAI' },
  { value: 'google', label: 'Google' },
  { value: 'ollama', label: 'Ollama' },
  { value: 'vllm', label: 'vLLM' },
  { value: 'custom', label: 'Custom' },
];

export const AUTH_OPTIONS: { value: AuthMethod; label: string; hint: string }[] = [
  { value: 'api_key', label: 'API Key', hint: 'Issue a gateway key to calling applications' },
  { value: 'oauth2', label: 'OAuth 2.0', hint: 'Token exchange against an identity provider' },
  { value: 'internal_identity', label: 'Internal Identity', hint: 'Trust Selfic workspace principals' },
];

export const GATEWAY_STATUS_OPTIONS: { value: GatewayStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'disabled', label: 'Disabled' },
];

export const ALL_API_TYPES: GatewayApiType[] = [
  'openai_chat_completions',
  'openai_responses',
  'openai_embeddings',
  'anthropic_messages',
  'legacy_completions',
  'custom',
];

export const API_TYPE_CATALOG: {
  id: GatewayApiType;
  label: string;
  endpoint: string;
  family: 'openai' | 'anthropic' | 'custom';
  hint: string;
}[] = [
  {
    id: 'openai_chat_completions',
    label: 'OpenAI Chat Completions',
    endpoint: '/v1/chat/completions',
    family: 'openai',
    hint: 'Chat messages with roles, tools, and streaming.',
  },
  {
    id: 'openai_responses',
    label: 'OpenAI Responses API',
    endpoint: '/v1/responses',
    family: 'openai',
    hint: 'Stateful responses with built-in tool calling.',
  },
  {
    id: 'openai_embeddings',
    label: 'OpenAI Embeddings',
    endpoint: '/v1/embeddings',
    family: 'openai',
    hint: 'Vector embeddings for retrieval and search.',
  },
  {
    id: 'anthropic_messages',
    label: 'Anthropic Messages API',
    endpoint: '/v1/messages',
    family: 'anthropic',
    hint: 'Claude-compatible messages and system prompts.',
  },
  {
    id: 'legacy_completions',
    label: 'Legacy Completions',
    endpoint: '/v1/completions',
    family: 'openai',
    hint: 'Prompt-in, text-out completions for older clients.',
  },
  {
    id: 'custom',
    label: 'Custom API',
    endpoint: 'Custom endpoint',
    family: 'custom',
    hint: 'A registered serving path unique to this model.',
  },
];

export const APP_CONNECTORS: AppConnector[] = [
  {
    id: 'claude_code',
    name: 'Claude Code',
    description: 'Anthropic CLI and desktop coding agent.',
    requiredApiTypes: ['anthropic_messages'],
  },
  {
    id: 'cursor',
    name: 'Cursor',
    description: 'OpenAI-compatible IDE assistant.',
    requiredApiTypes: ['openai_chat_completions'],
  },
  {
    id: 'github_copilot',
    name: 'GitHub Copilot',
    description: 'Copilot custom model via Chat Completions.',
    requiredApiTypes: ['openai_chat_completions'],
  },
  {
    id: 'langchain',
    name: 'LangChain',
    description: 'Framework adapters for chat, embeddings, and messages.',
    requiredApiTypes: ['openai_chat_completions', 'openai_responses', 'openai_embeddings', 'anthropic_messages'],
  },
  {
    id: 'custom',
    name: 'Custom Application',
    description: 'Any HTTP client using the selected gateway APIs.',
    requiredApiTypes: ALL_API_TYPES,
  },
];

const UNSUPPORTED_REASONS: Record<GatewayApiType, string> = {
  openai_chat_completions: 'This model is not registered with an OpenAI chat serving protocol.',
  openai_responses: 'The Responses API is not enabled for this model’s serving protocol.',
  openai_embeddings: 'Embeddings endpoints require a registered embedding model.',
  anthropic_messages: 'Anthropic Messages requires an Anthropic-registered chat model.',
  legacy_completions: 'Legacy Completions is only available for vLLM and completion-protocol models.',
  custom: 'Custom API is reserved for models registered with a custom serving protocol.',
};

export function llmTypeLabel(value: LlmType | ''): string {
  return LLM_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function providerLabel(value: LlmProvider): string {
  return PROVIDER_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function authLabel(value: AuthMethod): string {
  return AUTH_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function apiTypeLabel(value: GatewayApiType): string {
  return API_TYPE_CATALOG.find((item) => item.id === value)?.label ?? value;
}

export function servingProtocolLabel(value: ServingProtocol): string {
  const labels: Record<ServingProtocol, string> = {
    openai_chat: 'OpenAI Chat',
    openai_responses: 'OpenAI Responses',
    openai_embeddings: 'OpenAI Embeddings',
    anthropic_messages: 'Anthropic Messages',
    openai_completions: 'OpenAI Completions',
    custom: 'Custom protocol',
  };
  return labels[value];
}

export function statusLabel(value: GatewayStatus): string {
  return GATEWAY_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function defaultGovernance(): GovernanceSettings {
  return {
    enabled: true,
    rateLimiting: false,
    rateLimitPerMinute: 60,
    tokenQuotas: false,
    tokenQuotaMonthly: 1_000_000,
    auditLogging: true,
    piiDetection: false,
    piiMode: 'monitor',
    contentFiltering: false,
    contentFilterMode: 'monitor',
    gatewayInstructions: false,
    gatewayInstructionsText: '',
  };
}

export function normalizeGovernance(value?: Partial<GovernanceSettings> | null): GovernanceSettings {
  return { ...defaultGovernance(), ...value };
}

export function createEmptyForm(): GatewayFormState {
  return {
    name: '',
    llmType: '',
    modelId: '',
    apiTypes: [],
    customEndpoint: '',
    authMethod: 'api_key',
    governance: defaultGovernance(),
  };
}

export function resolveApiCompatibility(model: RegisteredLlm): ApiCompatibility[] {
  if (model.compatibility?.length) return model.compatibility;
  if (model.type === 'reranking') {
    return ALL_API_TYPES.map((apiType) =>
      apiType === 'custom'
        ? { apiType, badge: 'recommended' as const }
        : { apiType, badge: 'unsupported' as const, reason: 'Reranking models expose a custom ranking endpoint only.' }
    );
  }

  const supported = new Set<GatewayApiType>();
  const recommended: GatewayApiType[] = [];

  switch (model.servingProtocol) {
    case 'openai_chat':
      supported.add('openai_chat_completions');
      recommended.push('openai_chat_completions');
      if (model.provider === 'openai' || model.provider === 'azure_openai' || model.type === 'multimodal') {
        supported.add('openai_responses');
      }
      if (model.provider === 'vllm') {
        supported.add('legacy_completions');
      }
      break;
    case 'openai_responses':
      supported.add('openai_responses');
      supported.add('openai_chat_completions');
      recommended.push('openai_responses');
      break;
    case 'openai_embeddings':
      supported.add('openai_embeddings');
      recommended.push('openai_embeddings');
      break;
    case 'anthropic_messages':
      supported.add('anthropic_messages');
      recommended.push('anthropic_messages');
      break;
    case 'openai_completions':
      supported.add('legacy_completions');
      supported.add('openai_chat_completions');
      recommended.push('legacy_completions');
      break;
    case 'custom':
      supported.add('custom');
      recommended.push('custom');
      if (model.type === 'chat' || model.type === 'multimodal') {
        supported.add('openai_chat_completions');
      }
      break;
  }

  return ALL_API_TYPES.map((apiType) => {
    if (recommended.includes(apiType)) return { apiType, badge: 'recommended' as const };
    if (supported.has(apiType)) return { apiType, badge: 'compatible' as const };
    return { apiType, badge: 'unsupported' as const, reason: UNSUPPORTED_REASONS[apiType] };
  });
}

export function recommendedApiTypes(model: RegisteredLlm): GatewayApiType[] {
  return resolveApiCompatibility(model)
    .filter((item) => item.badge === 'recommended')
    .map((item) => item.apiType);
}

export function isApiSupported(model: RegisteredLlm, apiType: GatewayApiType): boolean {
  const match = resolveApiCompatibility(model).find((item) => item.apiType === apiType);
  return Boolean(match && match.badge !== 'unsupported');
}

export function isAppCompatible(app: AppConnector, selected: GatewayApiType[]): boolean {
  if (app.id === 'custom') return selected.length > 0;
  return app.requiredApiTypes.some((apiType) => selected.includes(apiType));
}

export function preferredAppApiType(app: AppConnector, selected: GatewayApiType[]): GatewayApiType | null {
  return app.requiredApiTypes.find((apiType) => selected.includes(apiType)) ?? selected[0] ?? null;
}

export function openaiFamilySelected(selected: GatewayApiType[]): boolean {
  return selected.some((apiType) => API_TYPE_CATALOG.find((item) => item.id === apiType)?.family === 'openai');
}

export function anthropicFamilySelected(selected: GatewayApiType[]): boolean {
  return selected.includes('anthropic_messages');
}

export function customFamilySelected(selected: GatewayApiType[]): boolean {
  return selected.includes('custom');
}

export function openaiBaseUrl(gatewayId: string): string {
  return `${GATEWAY_HOST}/gw/${gatewayId}/v1`;
}

export function anthropicBaseUrl(gatewayId: string): string {
  return `${GATEWAY_HOST}/gw/${gatewayId}/anthropic`;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function publicAliasFor(modelName: string, fallback: string): string {
  const slug = slugify(modelName || fallback) || 'model';
  return `gw-${slug}`;
}

export function compatibilityLabel(badge: CompatibilityBadge): string {
  if (badge === 'recommended') return 'Recommended';
  if (badge === 'compatible') return 'Compatible';
  return 'Not Supported';
}

export const GOVERNANCE_HELP = {
  enabled: 'Master switch for policy evaluation on this gateway.',
  rateLimiting: 'Cap inbound requests per minute per consumer.',
  tokenQuotas: 'Monthly token allowance before the gateway throttles or denies.',
  auditLogging: 'Persist request metadata for later review.',
  piiDetection: 'Scan prompts and completions for personal data.',
  contentFiltering: 'Block or flag disallowed prompt and completion content.',
  gatewayInstructions: 'System instructions the gateway prepends to every request for this model.',
  servingProtocol: 'The protocol this model was registered to serve.',
  publicAlias: 'Stable model name applications send to the gateway.',
};
